import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Beneficiary } from "../types/beneficiaries";
import { Goal } from "../types/goals";

export interface Contribution {
  beneficiaryId: number;
  amount: number;
}

// delete later
export interface BeneficiaryContribution {
  beneficiaryId: number;
  employeeContributionInDollars: number;
  employeeContributionInPercent: number;
  employerContributionInDollars: number;
  employerContributionInPercent: number;
  totalContributionPerPaycheck: number;
}
export interface ContributionDeduction {
  employeeContributionInDollars: number;
  employeeContributionInPercent: number;
  employerContributionInDollars: number;
  employerContributionInPercent: number;
  totalDeductionPerPaycheck: number;
  beneficiariesContributions: BeneficiaryContribution[];
}

interface WizardState {
  currentStep: number;
  currentContributionStep: number;
  beneficiaries: Beneficiary[];
  goals: Goal[];
  contributions: Contribution[];
  contributionDeductions: ContributionDeduction;
  shouldResetAllocations: boolean | null;
}

// Initial state
const initialState: WizardState = {
  currentStep: 1,
  currentContributionStep: 1,
  beneficiaries: [],
  goals: [],
  contributions: [],
  contributionDeductions: {
    employeeContributionInDollars: 0,
    employeeContributionInPercent: 0,
    employerContributionInDollars: 0,
    employerContributionInPercent: 0,
    totalDeductionPerPaycheck: 0,
    beneficiariesContributions: [],
  },
  shouldResetAllocations: null,
};

const wizardSlice = createSlice({
  name: "wizard",
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setCurrentContributionStep: (state, action: PayloadAction<number>) => {
      state.currentContributionStep = action.payload;
    },
    incrementCurrentStep: (state) => {
      if (state.currentStep !== undefined && state.currentStep !== null) {
        state.currentStep += 1;
      }
    },
    decrementCurrentStep: (state) => {
      if (state.currentStep !== undefined && state.currentStep !== null) {
        state.currentStep = Math.max(1, state.currentStep - 1);
      }
    },
    incrementCurrentContributionStep: (state) => {
      if (
        state.currentContributionStep !== undefined &&
        state.currentContributionStep !== null
      ) {
        state.currentContributionStep = Math.min(
          2,
          state.currentContributionStep + 1,
        );
      }
    },
    decrementCurrentContributionStep: (state) => {
      if (
        state.currentContributionStep !== undefined &&
        state.currentContributionStep !== null
      ) {
        state.currentContributionStep = Math.max(
          1,
          state.currentContributionStep - 1,
        );
      }
    },
    addBeneficiary: (state, action: PayloadAction<Beneficiary>) => {
      const exists = state.beneficiaries.some(
        (b) => b.id === action.payload.id,
      );
      if (!exists) {
        state.beneficiaries.push(action.payload);
      }
    },
    updateBeneficiary: (state, action: PayloadAction<Beneficiary>) => {
      const index = state.beneficiaries.findIndex(
        (b) => b.id === action.payload.id,
      );
      if (index !== -1) {
        state.beneficiaries[index] = action.payload;
      }
    },
    deleteBeneficiaryFromStore: (state, action: PayloadAction<number>) => {
      state.beneficiaries = state.beneficiaries.filter(
        (b) => b.id !== action.payload,
      );
      state.goals = state.goals.filter(
        (g) => g.beneficiaryId !== action.payload,
      );
      state.contributions = state.contributions.filter(
        (c) => c.beneficiaryId !== action.payload,
      );
    },

    setGoal: (state, action: PayloadAction<Goal>) => {
      const index = state.goals.findIndex(
        (g) => g.beneficiaryId === action.payload.beneficiaryId,
      );
      if (index !== -1) {
        state.goals[index] = action.payload;
      } else {
        state.goals.push(action.payload);
      }
    },
    setContribution: (state, action: PayloadAction<Contribution>) => {
      const index = state.contributions.findIndex(
        (c) => c.beneficiaryId === action.payload.beneficiaryId,
      );
      if (index !== -1) {
        state.contributions[index] = action.payload;
      } else {
        state.contributions.push(action.payload);
      }
    },
    setContributions: (state, action: PayloadAction<Contribution[]>) => {
      state.contributions = action.payload;
    },

    //delete later
    addEmployeeContribution: (
      state,
      action: PayloadAction<{
        employeeContributionInDollars?: number;
        employeeContributionInPercent?: number;
        employerContributionInDollars?: number;
        employerContributionInPercent?: number;
        totalDeductionPerPaycheck?: number;
      }>,
    ) => {
      state.contributionDeductions = {
        ...state.contributionDeductions,
        ...action.payload,
      };
    },

    addBeneficiaryContribution: (
      state,
      action: PayloadAction<BeneficiaryContribution>,
    ) => {
      state.contributionDeductions.beneficiariesContributions.push(
        action.payload,
      );
    },

    updateBeneficiaryContribution: (
      state,
      action: PayloadAction<{
        index: number;
        beneficiary: Partial<BeneficiaryContribution>;
      }>,
    ) => {
      const { index, beneficiary } = action.payload;
      if (
        index >= 0 &&
        index < state.contributionDeductions.beneficiariesContributions.length
      ) {
        state.contributionDeductions.beneficiariesContributions[index] = {
          ...state.contributionDeductions.beneficiariesContributions[index],
          ...beneficiary,
        };
      }
    },

    clearBeneficiariesContrbutions: (state) => {
      state.contributionDeductions.beneficiariesContributions = [];
    },

    setShouldResetAllocations: (state, action: PayloadAction<boolean>) => {
      state.shouldResetAllocations = action.payload;
    },

    clearShouldResetAllocations: (state) => {
      state.shouldResetAllocations = null;
    },
  },
});

// Export actions and reducer
export const {
  setCurrentStep,
  setCurrentContributionStep,
  incrementCurrentStep,
  incrementCurrentContributionStep,
  decrementCurrentStep,
  decrementCurrentContributionStep,
  addBeneficiary,
  updateBeneficiary,
  deleteBeneficiaryFromStore,
  setGoal,
  setContribution,
  setContributions,

  //delete these later
  addEmployeeContribution,
  addBeneficiaryContribution,
  updateBeneficiaryContribution,
  clearBeneficiariesContrbutions,

  setShouldResetAllocations,
  clearShouldResetAllocations,
} = wizardSlice.actions;

export default wizardSlice.reducer;
